import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditPencilCard } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as Plus } from '../../../assets/icons/plus.svg';
import { ReactComponent as UtilityProviderIcon } from '../../../assets/icons/sidebar/utility-icon.svg';
// import { ReactComponent as NewTabIcon } from '../../../assets/icons/tables/new-tab.svg';
// import { appCurrentBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  AccountsInterFace,
  RequestAccountInterface,
} from '../../../interface/account-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { disableUsersFilterApplied } from '../../../redux/slices/UsersManagementSlice';
import {
  fetchAllAccounts,
  fetchUtilityProviderFilters,
  setSortData,
  updateUtilityProviderFilters,
} from '../../../redux/slices/UtilityProviderManagementSlice';
import { RootState } from '../../../redux/store';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ConfirmBox from '../../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import UtilityDetailsSidePanel from '../../../shared-components/table-side-panel/UtilityDetailsSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
// import authApi from '../../../utils/api/auth';
import accountApi from '../../../utils/api/utility-provider';

import AddUtilityAccount from './add-utility-account';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

/**
 * Facility account list
 * @returns {any} The result
 */
function UtilityProviderList() {
  const { t } = useTranslation('account');
  const dispatch = useAppDispatch();
  const {
    accounts,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.utility);

  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [selectedUtilityId, setSelectedUtilityId] = useState<string | null>(null);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  /**
   * Fetch the account details in redus
   * @param {any} data - The data
   */
  const fetchData = (data: RequestAccountInterface) => {
    if (!loading) {
      dispatch(fetchAllAccounts(data));
    }
  };
  /**
   * Modal box close function
   * @param {any} message - The message
   */
  const closeModal = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenConfirm(false);
      setOpenStatus(false);
    }
  };

  /**
   * Add account function
   */
  const addAccount = () => {
    // setIsEditMode(false);
    setSidePanelOpen(true);
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: RequestAccountInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      ...value,
      sortField,
      sortBy,
    });
  };

  // /**
  //  * handleAvailabilityChange
  //  */
  // const handleAvailabilityChange = (name: string, value: string) => {
  //   setCurrentPage(value);
  // };

  /**
   * view facility function
   * @param currentData
   */
  // const viewUtilityFunction = (currentData: any) => {
  //   const accountDetails = accounts.find((value: AccountsInterFace) => value.id === currentData);
  //   if (accountDetails && Object.keys(accountDetails).length > 0) {
  //     const request: AccessUtilityPortal = {
  //       _id: accountDetails._id,
  //     };

  //     authApi
  //       .accessUtilityPortal(request)
  //       .then((response) => {
  //         const responseData = response?.data;
  //         const deployedType = window.origin.includes('test');

  //         window.open(
  //           `https://${appCurrentBranding}-utility${deployedType ? '-test' : ''}.web.app/login?accessToken=${responseData.access.token}&accessTokenExpires=${responseData.access.expires}&refreshToken=${responseData.refresh.token}&refreshTokenExpires=${responseData.refresh.expires}}`,
  //           '_blank',
  //         );
  //       })
  //       .catch((error) => {
  //         dispatch(
  //           setNotificationMessage({
  //             message: error?.response?.data?.message,
  //             status: true,
  //             type: error,
  //             code: error?.response?.data?.code,
  //           }),
  //         );
  //       });
  //   }
  // };

  /**
   * Set the target utility account and open the delete confirmation modal
   */
  const handleDeleteClick = (utilityId?: string | null) => {
    setSelectedUtilityId(utilityId || null);
    setOpenConfirm(true);
  };

  /**
   * Opens the side panel directly in edit mode for the given row
   */
  const handleEditFromRow = (currentRow: AccountsInterFace) => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.set('utilityId', currentRow.id);
    parameters.set('action', 'edit');
    setSearchParameters(parameters);
    setIsEditMode(true);
  };

  const column = [
    {
      label: t('userName'),
      accessorKey: 'name' as keyof AccountsInterFace,
      sortAllow: true,
      /**
       * No of stations
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: AccountsInterFace) => (
        <p className="hyper-link-coloum">{currentRow.name || '-'}</p>
      ),
    },
    {
      label: t('emailId'),
      accessorKey: 'email' as keyof AccountsInterFace,
      sortAllow: false,
    },
    {
      label: t('address'),
      accessorKey: 'address' as keyof AccountsInterFace,
      sortAllow: true,
    },
    {
      label: t('action'),
      accessorKey: '_id' as keyof AccountsInterFace,
      columnWidth: 110,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
      sticky: 'right' as const,
      /**
       * Row level actions - edit and delete
       * @param {AccountsInterFace} currentRow - The current row
       */
      customData: (currentRow: AccountsInterFace) =>
        currentRow?.id ? (
          <div className="action-buttons-wrap">
            {permissionCheck('cpoUtilityProviderAdd') && (
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditFromRow(currentRow);
                }}>
                <EditPencilCard className="icon edit" />
              </div>
            )}

            {permissionCheck('cpoUtilityProviderDelete') && (
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteClick(currentRow.id);
                }}>
                <DeleteIcon className="icon delete" />
              </div>
            )}
          </div>
        ) : null,
    },
  ];

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Column defination for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('userName'), accessorKey: 'name' },
    { label: t('emailId'), accessorKey: 'email' },
    { label: t('address'), accessorKey: 'address' },
    { label: t('created', 'Created'), accessorKey: 'createdAt' },
    { label: t('lastUpdated', 'Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function (hardcoded headers & fields)
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload: any = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    accountApi
      .exportData(payload)
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        // const rows = response.data.map((row: AccountsInterFace, index: number) => [
        //   index + 1,
        //   ConvertUTCtoDate(row?.createdAt || ''),
        //   row.name,
        //   row.email,
        //   row.address,
        //   'Utility Owner', // or dynamically use row.accountType if available
        // ]);
        const rows = response.data.map((row: AccountsInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row?.createdAt ? ConvertUTCtoDateTime(row.createdAt) : t('notAvailable');

              case 'name':
                return row.name || t('notAvailable');

              case 'email':
                return row.email || t('notAvailable');

              case 'address':
                return row.address || t('notAvailable');

              case 'updatedAt':
                return row.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : t('notAvailable');

              default:
                return (row as any)[col.accessorKey] || t('notAvailable');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, `${t('accountList')}.csv`);
          } else {
            exportPDF(headers, rows, `${t('accountList')}.pdf`);
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortField,
      sortBy,
    });
  };

  /**
   * Function to open side panel on row click
   * @param {any} utilityId - The utilityId
   */
  const handleOpenSidePanelOnRowClick = (utilityId: string) => {
    // Set or update the logId param
    searchParameters.set('utilityId', utilityId);
    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);

    dispatch(
      setPageTitle({
        title: t('utilityProvider'),
        icon: <UtilityProviderIcon />,
        count: undefined,
      }),
    );
  };

  /**
   * Handle changes to edit mode by updating state and URL parameter
   * @param {any} isEdit - The isEdit
   */
  const handleEditModeChange = (isEdit: boolean) => {
    setIsEditMode(isEdit);
    const newParameters = new URLSearchParams(searchParameters);
    if (isEdit) {
      newParameters.set('action', 'edit');
    } else {
      newParameters.delete('action');
    }
    setSearchParameters(newParameters);
  };

  /**
   * Delete confirm function
   */
  const deleteConfirmFunction = () => {
    const utilityId = selectedUtilityId || searchParameters.get('utilityId');
    if (!utilityId) return;

    accountApi
      .deleteAccount(utilityId)
      .then(() => {
        setOpenConfirm(false);
        closeModal(t('utilityProviderAccountDeletedSuccessfully'));
        handleRefresh();
        setSelectedUtilityId(null);

        const parameters = new URLSearchParams(searchParameters);
        parameters.delete('utilityId');
        setSearchParameters(parameters, { replace: true });
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Returns side panel header content: title, edit and delete actions.
   */
  const renderSidePanelTopLeftContent = () => {
    if (sidePanelOpen) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">{t('addUtility')}</p>
        </div>
      );
    }
    const utilityId = searchParameters.get('utilityId');
    if (!utilityId) return null;

    return (
      <div className="side-panel-header-left-title-actions">
        <p className="side-panel-header-title">
          {isEditMode ? t('editUtility') : t('utilityDetails')}
        </p>
        <div className="side-panel-header-left-icons">
          {!isEditMode && (
            <>
              {/* Edit Icon */}
              {permissionCheck('cpoUtilityProviderAdd') && (
                <div className="button-wrap">
                  <span
                    role="presentation"
                    className="access-facility"
                    onClick={() => handleEditModeChange(true)}>
                    <EditPencilCard className="icon" />
                  </span>
                  <div className="hover-label">{t('edit', 'Edit')}</div>
                </div>
              )}
              {/* Delete Icon */}
              {permissionCheck('cpoUtilityProviderDeleted') && (
                <div className="button-wrap">
                  <span
                    role="presentation"
                    className="access-facility"
                    onClick={() => handleDeleteClick(utilityId)}>
                    <DeleteIcon className="icon delete" />
                  </span>
                  <div className="hover-label">{t('delete', 'Delete')}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isFilterUpdated || accounts.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllAccounts({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        }),
      );
      dispatch(disableUsersFilterApplied());
    }
  }, [filters]);

  useEffect(() => {
    if (searchParameters.get('utilityId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('utilityId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!searchParameters.get('utilityId')) {
      setIsEditMode(false);
      if (searchParameters.get('action')) {
        const parameters = new URLSearchParams(searchParameters);
        parameters.delete('action');
        setSearchParameters(parameters, { replace: true });
      }
    }
  }, [searchParameters]);

  return (
    <div>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <ConfirmBox
        open={openConfirm}
        title={t('deleteUtilityConfirmTitle')}
        subTitle={t('deleteUtilityConfirmSubtitle')}
        conformSubmit={deleteConfirmFunction}
        closeModal={() => {
          setOpenConfirm(false);
          setSelectedUtilityId(null);
        }}
      />
      <CustomTable
        header={column}
        data={accounts}
        version="v2"
        limit={limit}
        page={page}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        created
        lastUpdated
        pageLimitChange={pageAndLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        actionButton
        filters={filters}
        filtersToDispatch={updateUtilityProviderFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchUtilityProviderFilters}
        sizeChangeButtonRequired
        actionDropdown
        exportData={accounts}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={
          permissionCheck('cpoUtilityProviderExport') ? exportDataPdfOrCSV : undefined
        }
        isExportRequired={permissionCheck('cpoUtilityProviderExport')}
        rightTopContent={
          <div className="table-bottom-button-list">
            <div className="table-button-item">
              <MyButton
                label={t('addUtility')}
                buttonType="submit"
                onClickFunc={addAccount}
                svgIcon={<Plus />}
                disableButton={!permissionCheck('cpoUtilityProviderAdd')}
              />
            </div>
          </div>
        }
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        refreshRequired
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
        rowClickEvent={handleOpenSidePanelOnRowClick}
        rowClickParamsKey="utilityId"
        sidePanelEnabled={sidePanelOpen}
        sidePanelManualContent={
          <AddUtilityAccount
            closeModal={closeModal}
            sidebarClosed={() => setSidePanelOpen(false)}
            hideHeader
          />
        }
        onSidePanelClose={() => {
          setSidePanelOpen(false);
          setIsEditMode(false);

          const parameters = new URLSearchParams(searchParameters);
          parameters.delete('action');
          parameters.delete('utilityId');
          setSearchParameters(parameters, { replace: true });

          dispatch(
            setPageTitle({
              title: t('utilityProvider'),
              icon: <UtilityProviderIcon />,
              count: totalResults,
            }),
          );
        }}
        rowClickSidePanel={
          <UtilityDetailsSidePanel
            isEditMode={isEditMode}
            onEditModeChange={handleEditModeChange}
            fetchIdFrom="queryParams"
            hideHeader
          />
        }
      />
    </div>
  );
}

export default UtilityProviderList;
